import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  OnInit
} from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { DividerModule } from 'primeng/divider';
import { MenuItem } from 'primeng/api';

import { TelecomService } from '@generated/services/telecom.service';
import { TelecomAccountResult } from '@generated/models/telecom-account-result';
import { VerifyTelecomAccountResult } from '@generated/models/verify-telecom-account-result';
import { VerifyCheck } from '@generated/models/verify-check';
import { SipTransport } from '@generated/models/sip-transport';
import { NotificationService } from '@core/services/notification.service';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { SipGatewayInfoComponent } from '@shared/components/sip-gateway-info/sip-gateway-info';
import { environment } from '../../../../environments/environment';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { TELECOM_ACCOUNT_FIELDS } from '@core/assistant/assistant-catalog';

const COUNTRY_RE = /^[A-Z]{2}$/;
const IPV4_OR_CIDR_RE = /^((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\/(3[0-2]|[12]?\d))?$/;

function patchPairValidator(userKey: string, pwdKey: string, errKey: string) {
  return (group: AbstractControl): ValidationErrors | null => {
    const uCtl = group.get(userKey);
    const pCtl = group.get(pwdKey);
    const u = uCtl?.value as string | null;
    const p = pCtl?.value as string | null;
    const uSet = !!(u && u.trim());
    const pSet = !!(p && p.trim());
    // Only validate when user edited at least one side — PATCH password
    // semantics: empty + untouched = "don't change", so we can't compare
    // it to username state in that case.
    const uDirty = uCtl?.dirty ?? false;
    const pDirty = pCtl?.dirty ?? false;
    if (!uDirty && !pDirty) return null;
    return uSet !== pSet ? { [errKey]: true } : null;
  };
}

@Component({
  selector: 'app-telecom-account-edit',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    SelectModule,
    InputTextModule,
    ProgressSpinnerModule,
    BreadcrumbModule,
    TooltipModule,
    TagModule,
    MessageModule,
    DividerModule,
    DeleteConfirmationComponent,
    SipGatewayInfoComponent
],
  templateUrl: './telecom-account-edit.html',
  styleUrl: './telecom-account-edit.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TelecomAccountEditComponent implements OnInit {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = TELECOM_ACCOUNT_FIELDS;

  private telecomService = inject(TelecomService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private notificationService = inject(NotificationService);

  readonly accountId = signal<string>('');
  readonly account = signal<TelecomAccountResult | null>(null);
  readonly isLoading = signal<boolean>(false);

  readonly verifyResult = signal<VerifyTelecomAccountResult | null>(null);
  readonly isVerifying = signal<boolean>(false);

  readonly isReprovisioning = signal<boolean>(false);
  readonly reprovisionConfirmVisible = signal<boolean>(false);

  readonly isDeleting = signal<boolean>(false);
  readonly deleteConfirmVisible = signal<boolean>(false);

  readonly isSaving = signal<boolean>(false);

  /** Название приложения для тайтлов и хинтов — чтоб UI не тёк по бренду. */
  readonly appName = environment.appName;

  readonly sipGateway = computed(() => this.account()?.sip_gateway ?? null);

  readonly hasOutboundAuthPassword = computed(() => this.account()?.custom_sip?.has_sip_outbound_auth_password ?? false);
  readonly hasInboundAuthPassword = computed(() => this.account()?.custom_sip?.has_sip_inbound_auth_password ?? false);

  readonly isCustom = computed(() => this.account()?.provider === 'Custom');

  readonly transportOptions: { label: string; value: SipTransport }[] = [
    { label: 'Auto', value: 'Auto' },
    { label: 'UDP', value: 'Udp' },
    { label: 'TCP', value: 'Tcp' },
    { label: 'TLS', value: 'Tls' }
  ];

  // Form for Custom-only edit fields (PATCH).
  // Note: name itself is not editable via PATCH in this iteration (spec doesn't list it).
  readonly editForm = new FormGroup(
    {
      sipServerAddress: new FormControl<string>('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
      sipTransport: new FormControl<SipTransport>('Auto', { nonNullable: true, validators: [Validators.required] }),
      sipDestinationCountry: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.pattern(COUNTRY_RE)] }),
      sipAllowedAddresses: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      // Outbound (LiveKit → provider): optional pair (Digest auth or IP-trust).
      // PATCH password: null = don't change, "" = disable (only if username
      // also empty), non-empty = replace.
      sipOutboundAuthUsername: new FormControl<string>('', { nonNullable: true }),
      sipOutboundAuthPassword: new FormControl<string>('', { nonNullable: true }),
      // Inbound (provider → LiveKit): optional pair. Cleared = disable.
      sipInboundAuthUsername: new FormControl<string>('', { nonNullable: true }),
      sipInboundAuthPassword: new FormControl<string>('', { nonNullable: true })
    },
    {
      validators: [
        patchPairValidator('sipOutboundAuthUsername', 'sipOutboundAuthPassword', 'outboundAuthPair'),
        patchPairValidator('sipInboundAuthUsername', 'sipInboundAuthPassword', 'inboundAuthPair')
      ]
    }
  );

  readonly allowedAddressesInvalid = signal<string[]>([]);

  readonly breadcrumbItems = computed<MenuItem[]>(() => [
    { label: $localize`:@@nav.phoneNumbers:Phone Numbers`, routerLink: '/phone-numbers' },
    { label: this.account()?.account_identifier || this.account()?.id || '' }
  ]);

  readonly homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.router.navigate(['/phone-numbers']);
      return;
    }
    this.accountId.set(id);
    this.loadAccount();
  }

  private loadAccount(): void {
    this.isLoading.set(true);
    this.telecomService.apiTelecomAccountsIdGet({ id: this.accountId() }).subscribe({
      next: (account) => {
        this.account.set(account);
        this.seedEditForm(account);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Failed to load telecom account:', error);
        this.notificationService.showError(
          $localize`:@@telecomAccount.notify.loadFail:Failed to load telecom account`
        );
        this.isLoading.set(false);
        if (error?.status === 404) {
          this.router.navigate(['/phone-numbers']);
        }
      }
    });
  }

  private seedEditForm(account: TelecomAccountResult): void {
    const cs = account.custom_sip;
    if (!cs) {
      this.editForm.reset({ sipTransport: 'Auto' });
      this.allowedAddressesInvalid.set([]);
      return;
    }
    this.editForm.reset({
      sipServerAddress: cs.sip_server_address ?? '',
      sipTransport: cs.sip_transport ?? 'Auto',
      sipDestinationCountry: cs.sip_destination_country ?? '',
      sipAllowedAddresses: (cs.sip_allowed_addresses ?? []).join(', '),
      sipOutboundAuthUsername: cs.sip_outbound_auth_username ?? '',
      sipOutboundAuthPassword: '',
      sipInboundAuthUsername: cs.sip_inbound_auth_username ?? '',
      sipInboundAuthPassword: ''
    });
    this.allowedAddressesInvalid.set([]);
  }

  // -- Verify --

  verify(): void {
    if (this.isVerifying()) return;
    this.isVerifying.set(true);
    this.verifyResult.set(null);
    this.telecomService.apiTelecomAccountsIdVerifyPost({ id: this.accountId() }).subscribe({
      next: (result) => {
        this.verifyResult.set(result);
        this.isVerifying.set(false);
      },
      error: (error) => {
        this.isVerifying.set(false);
        console.error('Verify failed:', error);
        this.notificationService.showError(
          $localize`:@@telecomAccount.notify.verifyFail:Verify failed`
        );
      }
    });
  }

  checkSeverity(check: VerifyCheck | undefined): 'success' | 'danger' | 'secondary' | 'info' {
    if (!check?.status) return 'info';
    switch (check.status) {
      case 'Ok': return 'success';
      case 'Failed': return 'danger';
      case 'Skipped': return 'secondary';
      default: return 'info';
    }
  }

  checkLabel(check: VerifyCheck | undefined): string {
    if (!check?.status) return $localize`:@@telecomAccount.verify.unknown:Unknown`;
    switch (check.status) {
      case 'Ok': return $localize`:@@telecomAccount.verify.ok:OK`;
      case 'Failed': return $localize`:@@telecomAccount.verify.failed:Failed`;
      case 'Skipped': return $localize`:@@telecomAccount.verify.skipped:Skipped`;
      default: return check.status as string;
    }
  }

  // -- Reprovision --

  askReprovision(): void {
    this.reprovisionConfirmVisible.set(true);
  }
  cancelReprovision(): void {
    this.reprovisionConfirmVisible.set(false);
  }
  confirmReprovision(): void {
    if (this.isReprovisioning()) return;
    this.isReprovisioning.set(true);
    this.telecomService.apiTelecomCustomAccountsIdReprovisionSipTrunksPost({ id: this.accountId() }).subscribe({
      next: (result) => {
        this.isReprovisioning.set(false);
        this.reprovisionConfirmVisible.set(false);
        const n = result.numbers_count ?? 0;
        this.notificationService.showSuccess(
          $localize`:@@telecomAccount.notify.reprovisioned:SIP trunks reprovisioned (${n}:count: numbers)`
        );
        // Refresh verify so user sees fresh state
        this.verify();
      },
      error: (error) => {
        this.isReprovisioning.set(false);
        this.reprovisionConfirmVisible.set(false);
        const msg = this.extractProblemDetailsMessage(error)
          ?? $localize`:@@telecomAccount.notify.reprovisionFail:Reprovision failed`;
        this.notificationService.showError(msg);
      }
    });
  }

  readonly reprovisionConfirmTitle = $localize`:@@telecomAccount.reprovision.title:Reprovision SIP trunks`;
  get reprovisionConfirmSubtitle(): string {
    return $localize`:@@telecomAccount.reprovision.subtitle:This deletes and recreates SIP trunks for this account. Active calls may be interrupted. Continue?`;
  }

  // -- Delete --

  askDelete(): void {
    this.deleteConfirmVisible.set(true);
  }
  cancelDelete(): void {
    this.deleteConfirmVisible.set(false);
  }
  confirmDelete(): void {
    if (this.isDeleting()) return;
    this.isDeleting.set(true);
    this.telecomService.apiTelecomAccountsIdDelete({ id: this.accountId() }).subscribe({
      next: () => {
        this.isDeleting.set(false);
        this.deleteConfirmVisible.set(false);
        this.notificationService.showSuccess(
          $localize`:@@telecomAccount.notify.deleted:Telecom account deleted`
        );
        this.router.navigate(['/phone-numbers']);
      },
      error: (error) => {
        this.isDeleting.set(false);
        this.deleteConfirmVisible.set(false);
        console.error('Delete failed:', error);
        this.notificationService.showError(
          $localize`:@@telecomAccount.notify.deleteFail:Failed to delete account`
        );
      }
    });
  }

  readonly deleteConfirmTitle = $localize`:@@telecomAccount.delete.title:Delete telecom account`;
  get deleteConfirmSubtitle(): string {
    const name = this.account()?.account_identifier || '';
    return $localize`:@@telecomAccount.delete.subtitle:Delete '${name}:name:'? All bound phone numbers and SIP trunks will also be removed. This cannot be undone.`;
  }

  // -- Edit (PATCH) --

  // The PATCH endpoint takes the same fields as create, but the GET only
  // returns base fields (account_identifier, provider, ...) and does NOT
  // return the current values of server / transport / country / addresses
  // / auth. So in this iteration we don't pre-populate the form — user
  // types fresh values and saves. We make this clear in the UI.
  // (If/when GET starts returning the full state, we'll seed editForm here.)

  private parseAllowedAddresses(raw: string): { values: string[]; invalid: string[] } {
    const parts = raw.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    const invalid = parts.filter(p => !IPV4_OR_CIDR_RE.test(p));
    return { values: parts, invalid };
  }

  saveEdit(): void {
    if (this.editForm.invalid || this.isSaving()) return;

    const raw = this.editForm.controls.sipAllowedAddresses.value;
    const { values: allowed, invalid } = this.parseAllowedAddresses(raw);
    this.allowedAddressesInvalid.set(invalid);
    if (allowed.length === 0 || invalid.length > 0) return;

    const v = this.editForm.getRawValue();
    const f = this.editForm.controls;

    // PATCH password semantics (same for outbound and inbound):
    //   null      → don't change
    //   ""        → disable auth (username must also be empty)
    //   non-empty → replace
    // PATCH username: send if dirty (null when empty = clear).
    const pwdField = (dirty: boolean, value: string): string | null =>
      dirty ? value : null;
    const userField = (dirty: boolean, value: string): string | null | undefined =>
      dirty ? (value.trim() || null) : undefined;

    this.isSaving.set(true);
    this.telecomService.apiTelecomCustomAccountsIdPatch({
      id: this.accountId(),
      body: {
        sip_server_address: v.sipServerAddress.trim() || null,
        sip_transport: v.sipTransport,
        sip_destination_country: v.sipDestinationCountry.trim().toUpperCase(),
        sip_allowed_addresses: allowed,
        sip_outbound_auth_username: userField(f.sipOutboundAuthUsername.dirty, v.sipOutboundAuthUsername),
        sip_outbound_auth_password: pwdField(f.sipOutboundAuthPassword.dirty, v.sipOutboundAuthPassword),
        sip_inbound_auth_username: userField(f.sipInboundAuthUsername.dirty, v.sipInboundAuthUsername),
        sip_inbound_auth_password: pwdField(f.sipInboundAuthPassword.dirty, v.sipInboundAuthPassword)
      }
    }).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.notificationService.showSuccess(
          $localize`:@@telecomAccount.notify.updated:Account updated`
        );
        this.router.navigate(['/phone-numbers']);
      },
      error: (error) => {
        this.isSaving.set(false);
        const msg = this.extractProblemDetailsMessage(error)
          ?? $localize`:@@telecomAccount.notify.updateFail:Update failed`;
        this.notificationService.showError(msg);
      }
    });
  }

  private extractProblemDetailsMessage(error: unknown): string | null {
    const err = error as { error?: { detail?: string; title?: string; errors?: Record<string, string[]> } } | null;
    if (!err?.error) return null;
    const errors = err.error.errors;
    if (errors) {
      const all = Object.values(errors).flat();
      if (all.length > 0) return all.join('; ');
    }
    return err.error.detail || err.error.title || null;
  }

  // Template helpers
  get f() {
    return this.editForm.controls;
  }
  isInvalid(controlName: keyof typeof this.f): boolean {
    const c = this.f[controlName];
    return c.invalid && (c.touched || c.dirty);
  }
  hasOutboundAuthPairError(): boolean {
    return !!this.editForm.errors?.['outboundAuthPair'] && this.editForm.touched;
  }
  hasInboundAuthPairError(): boolean {
    return !!this.editForm.errors?.['inboundAuthPair'] && this.editForm.touched;
  }

  get providerSeverity(): 'info' | 'success' | 'warn' | 'secondary' {
    switch (this.account()?.provider) {
      case 'Custom': return 'info';
      case 'Twilio': return 'success';
      case 'Voximplant': return 'warn';
      default: return 'secondary';
    }
  }

  get providerLabel(): string {
    switch (this.account()?.provider) {
      case 'Custom': return $localize`:@@telecomProvider.custom:Custom SIP`;
      case 'Twilio': return 'Twilio';
      case 'Voximplant': return 'Voximplant';
      default: return this.account()?.provider ?? '';
    }
  }
}
